import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Image from "next/legacy/image";
import Script from "next/script";

export default function Article({}) {
  const content = () => {
    return (
      <>
        <div className={styles["Article__image"]}>
          <Image
            src={
              "/static/images/articles/all-about-the-ernest-hemingway-house.jpg"
            }
            alt={"Statue of Hemingway"}
            width={900}
            height={565}
          />
        </div>
        <h1 className={styles["Article__heading"]}>
          All About the Ernest Hemingway House
        </h1>
        <ArticleAuthor />
        <p>
          The Hemingway house, once home to famous author Ernest Hemingway, is a
          popular historic landmark in Key West, Florida. Tourists and locals
          are able to visit the house to this day, as it has been converted into
          a museum where people can learn about Hemingway as they stand where he
          once lived. Hemingway was said to have written some of his best work
          in this home over the course of eight years, including his beloved
          novel <em>For Whom the Bell Tolls</em>. The life of Ernest Hemingway
          included many moves to and from different places, but while the author
          relocated often, his Key West home was one of the places he stayed the
          longest.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Early History</h2>
        <p>
          The Hemingway house was built by Asa Tift in 1851. He was a wreck
          salvager and marine architect who built the home out of limestone 16
          feet above sea level. With its high elevation and sturdy framework, it
          was able to survive hurricanes and other severe weather conditions
          with limited damage or flooding. It was built facing the Key West
          Lighthouse, another attraction that would become popular with modern
          tourists. After Tift's passing, the home was abandoned and boarded up
          until Hemingway and his wife came along. They carried out an extensive
          renovation project, including making the home one of the first on the
          island to have indoor plumbing. Hemingway's wife, Pauline, also had a
          swimming pool installed, the first in the Florida Keys.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Hemingway and the House
        </h2>
        <p>
          The Hemingways acquired their Key West home in 1931 when it was given
          to them as a wedding gift by Pauline's uncle. When searching for a
          house to live in long-term, Pauline had come across the foreclosed
          building, and despite describing it as "a damned haunted house," she
          convinced her uncle to make the purchase. The house cost a total of
          $8,000, which would be around $156,000 in today's dollars. The author
          and his wife stayed in the home for a total of eight years after
          living on and off in Key West for the previous three. The interior was
          mainly designed by Pauline, but all of the hunting trophies Hemingway
          brought home from his safari trips were also displayed. Among the
          activities that Hemingway partook in at his residence, aside from
          writing some of his best works, were swimming nude in the pool and
          holding boxing matches on the lawn. He was also said to have kept
          peacocks on the property. After his eight-year stay at the residence,
          he moved to Cuba in 1939, shortly before divorcing Pauline in 1940.
          She remained at the house until she died in 1951.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Modern Museum</h2>
        <p>
          After Ernest Hemingway died by suicide in 1961, the title to the house
          was passed down to his three sons, who auctioned the house off for
          $80,000. The new owners planned to live in the house, but they were
          overwhelmed by the public's fascination with the former home of the
          famous author. In 1964, they decided to open the house to the public
          as a museum. The museum does not have every original piece of
          furniture or d&eacute;cor from Hemingway's time at the house, as he
          and his family took much of what was there before the house was sold.
          But many of Hemingway's possessions did remain, especially larger,
          heavier fixtures in the home. The museum aims to depict the house as
          it was when Hemingway was living there, including re-creating his
          writing studio, which can only be viewed from behind a screen. The
          house is open seven days a week for tours and has become one of Key
          West's most popular attractions.
        </p>
        <h2 className={styles["Article__sub-heading"]}>Cats</h2>
        <p>
          Hemingway loved cats, and he had many of them at his Key West home.
          Many of these cats were the progeny of a white, six-toed cat named
          Snow White, given to him by Capt. Stanley Dexter, who believed that
          polydactyl cats were good luck, a common superstition among sailors.
          Over time, Hemingway accumulated as many as 150 cats between his Key
          West and Cuba homes. Today, there are around 60 cats living at the Key
          West property at any given time, about half of which also have
          polydactyly, and some of these are descendants of Snow White. The
          museum spays and neuters the majority of the cats but does allow for
          one litter to be born each year to keep the population going. The yard
          also features a drinking fountain for the cats, converted from a
          urinal once used in a bar Hemingway frequented. Cats can be spotted
          all around the property today, drinking from the fountain and
          wandering the museum grounds.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://vacationkw.com/blog-posts/2020/9/3/ernest-hemingway-and-key-west"
            >
              Hemingway and Key West
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.trustedtours.com/attractions/ernesthemingwayhome/"
            >
              Hemingway House History
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://hemingway.astate.edu/ernest-and-pauline/"
            >
              Ernest and Pauline Hemingway
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.biography.com/news/ernest-hemingway-key-west-home"
            >
              Ernest Hemingway's Key West Home
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.nytimes.com/1964/02/02/archives/hemingway-house-becomes-a-museum.html"
            >
              The Hemingway House Becomes a Museum
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://be.chewy.com/pet-parenting-pet-stories-hemingway-cats-the-felines-that-rule-papas-key-west-estate/"
            >
              Hemingway Cats: The Felines That Rule His Estate
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.wideopenpets.com/story-ernest-hemingway-six-toed-cats/"
            >
              The Story of Hemingway's Six-Toed Cats
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://blog.bookstellyouwhy.com/the-many-homes-of-ernest-hemingway"
            >
              The Many Homes of Ernest Hemingway
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.hemingwayhome.com/">
              Visit the Hemingway Home &amp; Museum
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://themaritimeexplorer.ca/2017/04/29/hemingway-house/"
            >
              All About the Hemingway House
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://keywest.floridaweekly.com/articles/hemingways-six-toed-cats/"
            >
              Hemingway and His Cats
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.conchtourtrain.com/ernest-hemingway.html"
            >
              Legendary Residents of Key West: Ernest Hemingway
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://theculturetrip.com/north-america/usa/florida/articles/a-brief-history-of-the-ernest-hemingway-house-in-key-west/"
            >
              Brief History of the Hemingway House
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.uncommoncaribbean.com/photo/hemingway-house/"
            >
              Hemingway House: A Singular Structure in the Southernmost City
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://travel.usnews.com/Key_West_FL/Things_To_Do/Ernest_Hemingway_Home_and_Museum_16767/"
            >
              Hemingway Museum Review
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.travelyesplease.com/travel-blog-ernest-hemingway-home/"
            >
              The Hemingway Museum and its Cats
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.cbsnews.com/news/a-federal-eye-on-hemingways-cats-why/"
            >
              A Federal Eye on Hemingway's Cats: Why?
            </a>
          </li>
          <li>
            <a href="https://www.realestateagents.com/fl/key-west">
              Real Estate Agents in Key West, Florida
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://exemplore.com/paranormal/Hemingways-Ghost-His-Haunted-Key-West-Home"
            >
              Hemingway's Haunted Key West Home
            </a>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "All About the Ernest Hemingway House: A Beautiful Piece of Real Estate in Key West | RealEstateAgents.com"
        }
        description={
          "Learn about the Ernest Hemingway House in Key West, Florida, including its history, Hemingway's time there, and its current use."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/all-about-the-ernest-hemingway-house",
          },
          headline:
            "All About the Ernest Hemingway House: A Beautiful Piece of Real Estate in Key West",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
